// Rain runtime-core constants. An agent's own vocabulary — its id, tool enums,
// visible-tool predicate and card renderers — belongs in `./agents/<agent>`.

export const MAX_FILE_SIZE_MB = 50;
export const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

/**
 * Rain's built-in `plan` tool source. Any agent that enables that source gets
 * these names, so they belong here rather than under `./agents/*` — quill2 and
 * sherlock both point `planMarkStepToolName` at `PlanMarkStep`.
 *
 * Deliberately absent from every profile's visible set: plan tools are
 * scaffolding surfaced through the Todo card, not tool-call cards in the
 * transcript.
 */
export enum ERainPlanTool {
  PlanCreate = 'plan_create',
  PlanActivate = 'plan_activate',
  PlanMarkStep = 'plan_mark_step',
  PlanDelete = 'plan_delete',
}

/**
 * Rain's own scaffolding, which no profile surfaces: artifact reads and writes
 * are staged as diffs and plan calls are shown through the Todo card. Dropping
 * one is always correct, so it is never reported as profile drift.
 */
export const isRainScaffoldingTool = (name: string): boolean =>
  name.startsWith('artifact_') || name.startsWith('plan_');

/** Builds an agent's `isVisibleTool` from the tool names it surfaces. */
export const buildVisibleToolPredicate = (
  visibleToolNames: Iterable<string>,
): ((name: string | undefined) => boolean) => {
  const names = new Set(visibleToolNames);
  return (name: string | undefined): boolean =>
    name !== undefined && names.has(name);
};

export const CLIENT_MESSAGE_ID_PREFIX = 'rain-';
export const TOOL_CALL_ID_PREFIX = 'rain-tool-';
export const INTERRUPT_ID_PREFIX = 'rain-interrupt-';
export const COMPACTION_ID_PREFIX = 'rain-compact-';
export const SLEEP_ID_PREFIX = 'rain-sleep-';
export const SUB_AGENT_TASK_ID_PREFIX = 'rain-task-';
export const FOLLOW_UP_ID_PREFIX = 'rain-followup-';
export const TRANSFER_MARKER_ID_PREFIX = 'rain-transfer-';
export const MAX_ITERATIONS_ID_PREFIX = 'rain-max-iterations-';
/** A folded run of work rows (see `utils/work-block.tsx`). */
export const WORK_BLOCK_ID_PREFIX = 'rain-work-';

export const STREAM_RETRY_MAX_ATTEMPTS = 5;
export const STREAM_RETRY_BASE_DELAY_MS = 1000;
export const STREAM_RETRY_MAX_DELAY_MS = 30_000;

/**
 * Stable `google.rpc.ErrorInfo` reason codes from the Rain API (`domain: "rain"`),
 * read off an error with `extractErrorReason`. Rain treats these as append-only
 * and never renames one once shipped, so branching on a reason is safe where
 * matching on the human-readable `message` is not.
 *
 * Add entries here as the UI starts handling them — this is deliberately not an
 * exhaustive mirror of the backend's list.
 */
export const RAIN_ERROR_REASONS = {
  /**
   * Deploy found nothing to promote: the thread's configuration already matches
   * the deployed agent (e.g. deploying twice with no edit in between). Arrives
   * as a 412 FAILED_PRECONDITION, and is a normal user action rather than a
   * fault — the UI should say "already up to date", not "deploy failed".
   */
  THREAD_DEPLOY_UP_TO_DATE: 'THREAD_DEPLOY_UP_TO_DATE',
} as const;

// Wire-format prefix that marks an interrupt answer as "user typed their own
// response" rather than a selected option. The Rain backend reads the prefix
// off the `selected` strings, so keep this in sync if the contract changes.
export const INTERRUPT_OTHER_PREFIX = 'Other: ';
